import { Alert, Button, Col, DatePicker, Descriptions, Divider, Drawer, Form, Input, InputNumber, Radio, Row, Select, Space, Switch, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { createProducerSchedule, getProducerScheduleRunSummary, nlToSchedule, updateProducerSchedule } from '../api';
import { KEYWORDS_TOKEN_SEPARATORS, PLATFORM_OPTIONS, normalizeKeywordsArray, normalizeOptionalString, normalizeStringArray, parseMultiline } from '../pages/contentProduce/shared';
import { formatErrorMessage, resolveErrorMessage } from '../utils/errorMessage';
import ProduceRunHistoryDrawer from './ProduceRunHistoryDrawer';
import ProducerRunViewer from './ProducerRunViewer';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PRODUCER_OPTIONS = [
  { label: '论文→小红书笔记', value: 'papers_to_notes' },
  { label: '论文→短视频', value: 'papers_to_video' },
  { label: '论文→公众号文章', value: 'papers_to_wechat_article' },
  { label: '小红书抓取→视频导入', value: 'xhs_video_importer' },
  { label: '视频双语转换', value: 'video_bilingual_convert' },
  { label: '创意→AI视频', value: 'idea_to_video' },
];

const VIDEO_PROVIDER_OPTIONS = [
  { label: '火山引擎 (seedance)', value: 'seedance' },
  { label: '阿里云万相 (Wan)', value: 'wan' },
  { label: '生数科技 (Vidu)', value: 'vidu' },
];

const VIDEO_ASPECT_RATIO_OPTIONS = [
  { label: '9:16（竖屏）', value: '9:16' },
  { label: '16:9（横屏）', value: '16:9' },
  { label: '3:4（竖构图）', value: '3:4' },
  { label: '4:3（横构图）', value: '4:3' },
  { label: '1:1（方形）', value: '1:1' },
];

const normalizeVideoProvider = (value) => {
  const key = String(normalizeOptionalString(value) || '').toLowerCase();
  if (key === 'wanx') return 'wan';
  if (key === 'volcengine') return 'seedance';
  return key;
};

const getDefaultParamsForProducer = (producerName) => {
  const base = {
    keywords: [],
    days: 7,
    max_count: 3,
    creator_user_id: 'producer_bot',
    available_platforms: [],
    extra_params: {},
  };

  if (producerName === 'papers_to_notes') {
    return {
      ...base,
      extra_params: {
        max_images: 9,
        force: false,
      },
    };
  }

  if (producerName === 'papers_to_video') {
    return {
      ...base,
      available_platforms: ['抖音', '小红书', '快手', '微信视频号'],
      extra_params: {
        scene_count: 6,
        duration_sec: 30,
        aspect_ratio: '9:16',
        output_language: 'zh',
        image_size: '',
        image_model: '',
        image_style: '',
        tts_voice: '',
        upload_images: false,
        force: false,
      },
    };
  }

  if (producerName === 'papers_to_wechat_article') {
    return {
      ...base,
      extra_params: {
        author: '',
        digest: '',
        tags: [],
        force: false,
      },
    };
  }

  if (producerName === 'xhs_video_importer') {
    return {
      ...base,
      extra_params: {
        mode: 'accounts',
        accountsText: '',
        headless: true,
        login_wait: 30,
        max_scroll: 30,
        scroll_pause: 1,
        note_list_type: 'published',
        video_only: true,
        deduplicate_across_keywords: true,
        deduplicate_across_accounts: true,
        force: false,
      },
    };
  }

  if (producerName === 'video_bilingual_convert') {
    return {
      ...base,
      extra_params: {
        target_lang: 'auto',
        max_items: 5,
        filters: { content_type: ['视频'], status: ['待发布'] },
        force: false,
      },
    };
  }

  if (producerName === 'idea_to_video') {
    return {
      ...base,
      days: 1,
      max_count: 1,
      available_platforms: ['抖音', '小红书', '快手', '微信视频号'],
      extra_params: {
        idea_text: '',
        video_provider: 'seedance',
        aspect_ratio: '9:16',
        duration_preset: '~30s',
        duration_sec: 30,
        style: '真实电影感',
        model_name: '',
        storyboard_count: 6,
        include_subtitles: true,
        referencesText: '',
        first_frame_source: '',
        last_frame_source: '',
        tts_voice: '',
        force: false,
      },
    };
  }

  return base;
};

const toIso = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value.toISOString === 'function') return value.toISOString();
  return String(value);
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-';
  const raw = String(dateTimeString);
  const normalized = raw.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/, '$1$2');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const TRIGGER_TYPE_LABEL = {
  cron: 'Cron',
  interval: '间隔',
  fixed_time: '一次',
  fixed: '一次',
  once: '一次',
};

const statusTag = (enabled) => (enabled ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>);

const buildVideoConvertFilters = (filters = {}) => {
  const out = {};
  const contentType = filters.content_type;
  if (contentType && contentType.length) out.content_type = contentType;
  const source = filters.source;
  if (source && source.length) out.source = source;
  const creator = normalizeOptionalString(filters.creator_user_id);
  if (creator) out.creator_user_id = creator;
  const status = filters.status;
  if (status && status.length) out.status = status;
  if (filters.risk_flag !== undefined && filters.risk_flag !== null) out.risk_flag = filters.risk_flag;
  if (filters.is_manual_reviewed !== undefined && filters.is_manual_reviewed !== null) out.is_manual_reviewed = filters.is_manual_reviewed;
  if (filters.manual_review_required !== undefined && filters.manual_review_required !== null) out.manual_review_required = filters.manual_review_required;
  const tags = filters.tags;
  if (tags) {
    const tagsArray = typeof tags === 'string'
      ? tags.split(',').map((t) => t.trim()).filter(Boolean)
      : tags;
    if (tagsArray.length > 0) out.tags = tagsArray;
  }
  const platforms = filters.available_platforms;
  if (platforms && platforms.length) out.available_platforms = platforms;
  const title = normalizeOptionalString(filters.title);
  if (title) out.title = title;
  const contentId = normalizeOptionalString(filters.content_id);
  if (contentId) out.content_id = contentId;
  const range = filters.ingested_at_range;
  if (range && range.length === 2) {
    const fromDate = range[0]?.toDate ? range[0].toDate() : range[0];
    const toDate = range[1]?.toDate ? range[1].toDate() : range[1];
    if (fromDate && toDate) {
      out.ingested_at_range = {
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
      };
    }
  }
  return Object.keys(out).length ? out : undefined;
};

const buildParamsForProducer = (producerName, params = {}) => {
  const base = {
    keywords: normalizeKeywordsArray(params.keywords),
    days: params.days,
    max_count: params.max_count,
    creator_user_id: normalizeOptionalString(params.creator_user_id),
    available_platforms: normalizeStringArray(params.available_platforms),
    extra_params: { ...(params.extra_params || {}) },
  };

  if (producerName === 'papers_to_wechat_article') {
    base.extra_params = {
      author: normalizeOptionalString(base.extra_params.author),
      digest: normalizeOptionalString(base.extra_params.digest),
      tags: normalizeStringArray(base.extra_params.tags),
      force: !!base.extra_params.force,
    };
  }

  if (producerName === 'papers_to_notes') {
    base.extra_params = {
      max_images: base.extra_params.max_images,
      force: !!base.extra_params.force,
    };
  }

  if (producerName === 'papers_to_video') {
    base.extra_params = {
      scene_count: base.extra_params.scene_count,
      duration_sec: base.extra_params.duration_sec,
      aspect_ratio: normalizeOptionalString(base.extra_params.aspect_ratio) || '9:16',
      output_language: normalizeOptionalString(base.extra_params.output_language) || 'zh',
      image_size: normalizeOptionalString(base.extra_params.image_size),
      image_model: normalizeOptionalString(base.extra_params.image_model),
      image_style: normalizeOptionalString(base.extra_params.image_style),
      tts_voice: normalizeOptionalString(base.extra_params.tts_voice),
      upload_images: !!base.extra_params.upload_images,
      force: !!base.extra_params.force,
    };
  }

  if (producerName === 'xhs_video_importer') {
    const mode = base.extra_params.mode || 'accounts';
    const accounts = parseMultiline(base.extra_params.accountsText);
    base.keywords = mode === 'keywords' ? base.keywords : [];
    base.extra_params = {
      accounts: mode === 'accounts' ? accounts : [],
      headless: !!base.extra_params.headless,
      login_wait: base.extra_params.login_wait,
      max_scroll: base.extra_params.max_scroll,
      scroll_pause: base.extra_params.scroll_pause,
      note_list_type: base.extra_params.note_list_type,
      video_only: base.extra_params.video_only !== false,
      deduplicate_across_keywords: base.extra_params.deduplicate_across_keywords !== false,
      deduplicate_across_accounts: base.extra_params.deduplicate_across_accounts !== false,
      force: !!base.extra_params.force,
    };
    delete base.extra_params.mode;
    delete base.extra_params.accountsText;
  }

  if (producerName === 'video_bilingual_convert') {
    base.extra_params = {
      target_lang: normalizeOptionalString(base.extra_params.target_lang) || 'auto',
      max_items: base.extra_params.max_items,
      filters: buildVideoConvertFilters(base.extra_params.filters || {}),
      force: !!base.extra_params.force,
    };
  }

  if (producerName === 'idea_to_video') {
    base.extra_params = {
      idea_text: normalizeOptionalString(base.extra_params.idea_text),
      video_provider: normalizeVideoProvider(base.extra_params.video_provider) || 'seedance',
      aspect_ratio: normalizeOptionalString(base.extra_params.aspect_ratio) || '9:16',
      duration_preset: normalizeOptionalString(base.extra_params.duration_preset) || '~30s',
      duration_sec: base.extra_params.duration_sec,
      style: normalizeOptionalString(base.extra_params.style),
      model_name: normalizeOptionalString(base.extra_params.model_name),
      storyboard_count: base.extra_params.storyboard_count,
      include_subtitles: base.extra_params.include_subtitles !== false,
      reference_sources: parseMultiline(base.extra_params.referencesText),
      tts_voice: normalizeOptionalString(base.extra_params.tts_voice),
      force: !!base.extra_params.force,
    };
    const firstFrameSource = normalizeOptionalString(base.extra_params.first_frame_source);
    const lastFrameSource = normalizeOptionalString(base.extra_params.last_frame_source);
    if (firstFrameSource) {
      base.extra_params.reference_sources = [
        firstFrameSource,
        ...(base.extra_params.reference_sources || []),
      ];
    }
    if (lastFrameSource) {
      base.extra_params.reference_sources = [
        ...(base.extra_params.reference_sources || []),
        lastFrameSource,
      ];
    }
  }

  base.extra_params = Object.fromEntries(Object.entries(base.extra_params || {}).filter(([, v]) => v !== undefined));
  return base;
};

const ProducerScheduleEditorModal = ({
  open,
  onClose,
  onSaved,
  initialSchedule,
  defaultProducerName = 'papers_to_notes',
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [parsingSchedule, setParsingSchedule] = useState(false);
  const [scheduleParseResult, setScheduleParseResult] = useState(null);
  const [activeTab, setActiveTab] = useState('edit');
  const [runSummaryLoading, setRunSummaryLoading] = useState(false);
  const [runSummary, setRunSummary] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [runViewerOpen, setRunViewerOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState('');
  const producerName = Form.useWatch('producer_name', form);
  const xhsMode = Form.useWatch(['params', 'extra_params', 'mode'], form);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialSchedule ? 'info' : 'edit');
    const init = initialSchedule || null;
    const effectiveProducerName = init?.producer_name || defaultProducerName;
    const defaultParams = getDefaultParamsForProducer(effectiveProducerName);
    const initExtraParams = init?.params?.extra_params || {};
    const normalizedIdeaVideoProvider = effectiveProducerName === 'idea_to_video'
      ? (normalizeVideoProvider(initExtraParams.video_provider) || defaultParams.extra_params.video_provider || 'seedance')
      : undefined;

    form.setFieldsValue({
      schedule_id: init?.schedule_id,
      task_name: init?.task_name,
      producer_name: effectiveProducerName,
      enabled: init?.enabled ?? true,
      schedule: {
        trigger_type: init?.schedule?.trigger_type || 'cron',
        timezone: init?.schedule?.timezone || 'Asia/Shanghai',
        cron_expression: init?.schedule?.cron_expression,
        interval_seconds: init?.schedule?.interval_seconds,
        start_at: init?.schedule?.start_at,
        end_at: init?.schedule?.end_at,
        run_at: init?.schedule?.run_at,
      },
      params: {
        keywords: init?.params?.keywords ?? defaultParams.keywords,
        days: init?.params?.days ?? defaultParams.days,
        max_count: init?.params?.max_count ?? defaultParams.max_count,
        creator_user_id: init?.params?.creator_user_id ?? defaultParams.creator_user_id,
        available_platforms: init?.params?.available_platforms ?? defaultParams.available_platforms,
        extra_params: {
          ...defaultParams.extra_params,
          ...initExtraParams,
          video_provider: normalizedIdeaVideoProvider,
          mode: effectiveProducerName === 'xhs_video_importer'
            ? (initExtraParams.mode || defaultParams.extra_params.mode)
            : (defaultParams.extra_params.mode || 'accounts'),
          accountsText: effectiveProducerName === 'xhs_video_importer'
            ? ((initExtraParams.accounts || []).join('\n') || defaultParams.extra_params.accountsText || '')
            : (defaultParams.extra_params.accountsText || ''),
        },
      },
    });
    setScheduleParseResult(null);
  }, [defaultProducerName, form, initialSchedule, open]);

  useEffect(() => {
    if (!open) return;
    const scheduleId = String(initialSchedule?.schedule_id || '').trim();
    if (!scheduleId) {
      setRunSummary(null);
      return;
    }
    let mounted = true;
    (async () => {
      setRunSummaryLoading(true);
      try {
        const resp = await getProducerScheduleRunSummary({ schedule_ids: [scheduleId] });
        const item = resp?.status === 'success' ? resp?.items?.[scheduleId] : null;
        if (mounted) setRunSummary(item || null);
      } catch (e) {
        if (mounted) setRunSummary(null);
      } finally {
        if (mounted) setRunSummaryLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [initialSchedule?.schedule_id, open]);

  useEffect(() => {
    if (!open) return;
    if (!producerName) return;
    if (initialSchedule) return;

    const defaultParams = getDefaultParamsForProducer(producerName);
    const currentParams = form.getFieldValue('params') || {};
    const currentExtra = currentParams.extra_params || {};

    form.setFieldsValue({
      params: {
        ...currentParams,
        keywords: currentParams.keywords ?? defaultParams.keywords,
        days: currentParams.days ?? defaultParams.days,
        max_count: currentParams.max_count ?? defaultParams.max_count,
        creator_user_id: currentParams.creator_user_id ?? defaultParams.creator_user_id,
        available_platforms: currentParams.available_platforms ?? defaultParams.available_platforms,
        extra_params: {
          ...defaultParams.extra_params,
          ...(producerName === 'xhs_video_importer'
            ? {
              mode: currentExtra.mode ?? defaultParams.extra_params.mode,
              accountsText: currentExtra.accountsText ?? defaultParams.extra_params.accountsText,
            }
            : {}),
        },
      },
    });
  }, [form, initialSchedule, open, producerName]);

  const handleParseSchedule = async () => {
    try {
      const values = await form.validateFields(['schedule_text']);
      const text = values.schedule_text?.trim();

      if (!text) {
        message.warning('请输入定时描述');
        return;
      }

      setParsingSchedule(true);
      setScheduleParseResult(null);

      const response = await nlToSchedule({ text });

      if (response.status === 'success') {
        const parsed = response.schedule || {};
        const parsedTrigger = String(parsed.trigger_type || '').trim().toLowerCase();
        const nextTriggerType = parsedTrigger === 'cron'
          ? 'cron'
          : parsedTrigger === 'interval'
            ? 'interval'
            : 'fixed_time';

        form.setFieldsValue({
          schedule: {
            ...(form.getFieldValue('schedule') || {}),
            trigger_type: nextTriggerType,
            cron_expression: parsed.cron_expression || undefined,
            run_at: parsed.run_at || undefined,
            interval_seconds: parsed.interval_seconds || undefined,
            start_at: parsed.start_at || undefined,
            end_at: parsed.end_at || undefined,
            timezone: parsed.timezone || 'Asia/Shanghai',
          },
        });

        setScheduleParseResult({ success: true, data: response });
        message.success('解析成功');
      } else if (response.status === 'needs_clarification') {
        setScheduleParseResult({ success: false, needsClarification: true, data: response });
        message.warning('需要补充信息');
      } else {
        setScheduleParseResult({ success: false, data: response });
        message.error('解析失败');
      }
    } catch (error) {
      if (error?.errorFields) return;
      console.error('解析定时描述失败:', error);
      message.error('解析失败，请稍后重试');
      setScheduleParseResult({ success: false, error: error?.message || String(error) });
    } finally {
      setParsingSchedule(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const scheduleId = values.schedule_id;
      const taskName = normalizeOptionalString(values.task_name);
      const name = String(values.producer_name || '').trim();
      const enabled = !!values.enabled;

      if (!scheduleId && !scheduleParseResult?.success) {
        message.warning('请先输入定时描述并点击“解析”');
        return;
      }

      const schedule = { ...(values.schedule || {}) };
      schedule.trigger_type = String(schedule.trigger_type || 'cron');
      schedule.timezone = normalizeOptionalString(schedule.timezone) || undefined;
      schedule.cron_expression = normalizeOptionalString(schedule.cron_expression);
      schedule.interval_seconds = schedule.interval_seconds === undefined ? undefined : Number(schedule.interval_seconds);
      schedule.start_at = toIso(schedule.start_at);
      schedule.end_at = toIso(schedule.end_at);
      schedule.run_at = toIso(schedule.run_at);

      if (schedule.trigger_type === 'cron') {
        delete schedule.interval_seconds;
        delete schedule.start_at;
        delete schedule.end_at;
        delete schedule.run_at;
      } else if (schedule.trigger_type === 'interval') {
        delete schedule.cron_expression;
        delete schedule.run_at;
      } else if (schedule.trigger_type === 'fixed_time') {
        delete schedule.cron_expression;
        delete schedule.interval_seconds;
        delete schedule.start_at;
        delete schedule.end_at;
      }

      const params = buildParamsForProducer(name, values.params || {});

      let resp;
      if (scheduleId) {
        resp = await updateProducerSchedule({
          schedule_id: scheduleId,
          task_name: taskName,
          producer_name: name,
          enabled,
          schedule,
          params,
        });
      } else {
        resp = await createProducerSchedule({
          task_name: taskName,
          producer_name: name,
          enabled,
          schedule,
          params,
        });
      }

      if (resp?.status === 'success') {
        message.success('已保存');
        onSaved?.();
        onClose?.();
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '保存失败'));
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const scheduleId = String(initialSchedule?.schedule_id || '').trim();
  const infoSchedule = initialSchedule || null;
  const infoParams = infoSchedule?.params || {};
  const infoScheduleRule = infoSchedule?.schedule || {};
  const activeRun = runSummary?.active_run;
  const latestRun = runSummary?.latest_run;

  return (
    <Drawer
      title={initialSchedule ? '编辑生产任务' : '新建生产任务'}
      placement="right"
      width={800}
      onClose={onClose}
      open={open}
      destroyOnClose
      mask={true}
      maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
      footer={(
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" onClick={handleOk} loading={saving}>
              保存
            </Button>
          </Space>
        </div>
      )}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'info',
            label: '任务信息',
            children: (
              <div style={{ paddingTop: 8 }}>
                {!infoSchedule ? (
                  <Alert type="info" showIcon message="新建任务：保存后可查看任务信息与运行情况" />
                ) : (
                  <>
                    <Descriptions
                      size="small"
                      column={2}
                      bordered
                      style={{ marginBottom: 12 }}
                      items={[
                        {
                          key: 'schedule_id',
                          label: '任务ID',
                          children: scheduleId ? <Text code copyable={{ text: scheduleId }}>{scheduleId}</Text> : '-',
                        },
                        {
                          key: 'status',
                          label: '状态',
                          children: statusTag(!!infoSchedule?.enabled),
                        },
                        {
                          key: 'task_name',
                          label: '任务名称',
                          children: (
                            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
                              {infoSchedule?.task_name || '-'}
                            </div>
                          ),
                        },
                        {
                          key: 'producer',
                          label: '生产能力',
                          children: infoSchedule?.producer_name || '-',
                        },
                        {
                          key: 'trigger_type',
                          label: '触发方式',
                          children: TRIGGER_TYPE_LABEL[String(infoScheduleRule?.trigger_type || 'cron')] || String(infoScheduleRule?.trigger_type || 'cron'),
                        },
                        {
                          key: 'schedule',
                          label: '定时规则',
                          children: (
                            <div style={{ lineHeight: 1.5 }}>
                              {String(infoScheduleRule?.trigger_type || 'cron') === 'cron' && (
                                <div>Cron：{infoScheduleRule?.cron_expression || '-'}</div>
                              )}
                              {String(infoScheduleRule?.trigger_type || '') === 'interval' && (
                                <div>间隔：{infoScheduleRule?.interval_seconds ?? '-'} 秒</div>
                              )}
                              {['fixed_time', 'fixed', 'once'].includes(String(infoScheduleRule?.trigger_type || '')) && (
                                <div>执行时间：{formatDateTime(infoScheduleRule?.run_at)}</div>
                              )}
                              {infoScheduleRule?.timezone && <div>时区：{infoScheduleRule?.timezone}</div>}
                              {(infoScheduleRule?.start_at || infoScheduleRule?.end_at) && (
                                <div>
                                  区间：{formatDateTime(infoScheduleRule?.start_at)} ～ {formatDateTime(infoScheduleRule?.end_at)}
                                </div>
                              )}
                            </div>
                          ),
                        },
                        {
                          key: 'created_at',
                          label: '创建时间',
                          children: formatDateTime(infoSchedule?.created_at),
                        },
                        {
                          key: 'updated_at',
                          label: '更新时间',
                          children: formatDateTime(infoSchedule?.updated_at),
                        },
                      ]}
                    />

                    <Descriptions
                      size="small"
                      column={2}
                      bordered
                      style={{ marginBottom: 12 }}
                      title="生产参数"
                      items={[
                        {
                          key: 'keywords',
                          label: '关键词',
                          children: Array.isArray(infoParams?.keywords) && infoParams.keywords.length > 0
                            ? infoParams.keywords.join(', ')
                            : '-',
                        },
                        { key: 'days', label: '最近 N 天', children: infoParams?.days ?? '-' },
                        { key: 'max_count', label: '最多处理条数', children: infoParams?.max_count ?? '-' },
                        { key: 'creator_user_id', label: '创建者ID', children: infoParams?.creator_user_id || '-' },
                        {
                          key: 'available_platforms',
                          label: '可投递平台',
                          children: Array.isArray(infoParams?.available_platforms) && infoParams.available_platforms.length > 0
                            ? infoParams.available_platforms.join(', ')
                            : '-',
                        },
                        {
                          key: 'extra_params',
                          label: '额外参数',
                          span: 2,
                          children: (
                            <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(infoParams?.extra_params || {}, null, 2)}</pre>}>
                              <Text code style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {JSON.stringify(infoParams?.extra_params || {})}
                              </Text>
                            </Tooltip>
                          ),
                        },
                      ]}
                    />

                    <Descriptions
                      size="small"
                      column={2}
                      bordered
                      title="运行情况"
                      items={[
                        {
                          key: 'running',
                          label: '运行中',
                          children: runSummaryLoading ? <Text>加载中...</Text> : (activeRun ? <Tag color="gold">运行中</Tag> : <Tag>-</Tag>),
                        },
                        {
                          key: 'active_run_id',
                          label: '当前运行ID',
                          children: activeRun?.run_id ? <Text code copyable={{ text: activeRun.run_id }}>{activeRun.run_id}</Text> : '-',
                        },
                        {
                          key: 'latest_status',
                          label: '最近运行状态',
                          children: latestRun?.status ? <Tag color={latestRun.status === 'succeeded' ? 'green' : latestRun.status === 'failed' ? 'red' : latestRun.status === 'running' ? 'gold' : 'default'}>{latestRun.status}</Tag> : '-',
                        },
                        {
                          key: 'latest_updated_at',
                          label: '最近运行时间',
                          children: formatDateTime(latestRun?.updated_at || latestRun?.created_at),
                        },
                        {
                          key: 'actions',
                          label: '操作',
                          span: 2,
                          children: (
                            <Space>
                              <Button
                                icon={<HistoryOutlined />}
                                onClick={() => setHistoryOpen(true)}
                                disabled={!scheduleId}
                              >
                                运行历史
                              </Button>
                              <Button
                                icon={<EyeOutlined />}
                                onClick={() => {
                                  const rid = activeRun?.run_id || latestRun?.run_id;
                                  if (!rid) return;
                                  setActiveRunId(rid);
                                  setRunViewerOpen(true);
                                }}
                                disabled={!activeRun?.run_id && !latestRun?.run_id}
                              >
                                查看运行详情
                              </Button>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  </>
                )}
              </div>
            ),
          },
          {
            key: 'edit',
            label: '编辑',
            children: null,
          },
        ]}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          enabled: true,
          schedule: { trigger_type: 'cron', timezone: 'Asia/Shanghai' },
          params: { days: 7, max_count: 3, creator_user_id: 'producer_bot', extra_params: {} },
        }}
        style={{ display: activeTab === 'edit' ? 'block' : 'none' }}
      >
        <Form.Item name="schedule_id" hidden>
          <Input />
        </Form.Item>

        {/* schedule 字段不对普通用户展示，但仍需要随请求提交 */}
        <Form.Item name={['schedule', 'trigger_type']} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={['schedule', 'timezone']} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={['schedule', 'cron_expression']} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={['schedule', 'interval_seconds']} hidden>
          <InputNumber />
        </Form.Item>
        <Form.Item name={['schedule', 'start_at']} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={['schedule', 'end_at']} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={['schedule', 'run_at']} hidden>
          <Input />
        </Form.Item>

        <Space size="middle" style={{ width: '100%' }}>
          <Form.Item name="task_name" label="任务名称（可选）" style={{ flex: 1 }}>
            <Input placeholder="例如：论文→小红书笔记（每日 9 点）" allowClear />
          </Form.Item>
          <Form.Item
            name="producer_name"
            label="生产类型"
            rules={[{ required: true, message: '请选择生产类型' }]}
            style={{ flex: 1 }}
          >
            <Select options={PRODUCER_OPTIONS} popupMatchSelectWidth={false} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>

        <Divider style={{ margin: '8px 0 12px' }} />

        <Form.Item
          name="schedule_text"
          label="定时描述（自然语言）"
          tooltip="推荐填写自然语言并点击“解析”，系统会自动生成定时规则"
        >
          <Input.TextArea
            rows={4}
            placeholder={[
              '示例（Cron）：每周一到周五 9:00 执行',
              '示例（一次）：2026-01-15 14:30 执行一次',
              '示例（间隔）：从现在开始每 2 小时执行一次，持续 3 天',
            ].join('\n')}
          />
        </Form.Item>

        <Space style={{ marginBottom: 12 }}>
          <Button onClick={handleParseSchedule} loading={parsingSchedule}>
            解析
          </Button>
        </Space>

        {scheduleParseResult && (
          <div style={{ marginBottom: 12 }}>
            {scheduleParseResult.success ? (
              <div>
                <Alert
                  message="解析成功"
                  description="定时任务文本解析成功，请确认以下信息："
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                {scheduleParseResult.data?.schedule && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>定时规则：</div>
                    <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginBottom: 8 }}>
                      <div>
                        触发类型：
                        {scheduleParseResult.data.schedule.trigger_type === 'cron'
                          ? '定时任务 (Cron)'
                          : scheduleParseResult.data.schedule.trigger_type === 'interval'
                            ? '间隔任务 (Interval)'
                            : '单次任务'}
                      </div>
                      {scheduleParseResult.data.schedule.cron_expression && (
                        <div>Cron表达式：{scheduleParseResult.data.schedule.cron_expression}</div>
                      )}
                      {scheduleParseResult.data.schedule.run_at && (
                        <div>执行时间：{scheduleParseResult.data.schedule.run_at}</div>
                      )}
                      {scheduleParseResult.data.schedule.interval_seconds && (
                        <div>间隔（秒）：{scheduleParseResult.data.schedule.interval_seconds}</div>
                      )}
                      {(scheduleParseResult.data.schedule.start_at || scheduleParseResult.data.schedule.end_at) && (
                        <div>
                          区间：
                          {scheduleParseResult.data.schedule.start_at ? `起始 ${scheduleParseResult.data.schedule.start_at}` : '起始不限'}
                          {' ～ '}
                          {scheduleParseResult.data.schedule.end_at ? `结束 ${scheduleParseResult.data.schedule.end_at}` : '结束不限'}
                        </div>
                      )}
                      <div>时区：{scheduleParseResult.data.schedule.timezone || 'Asia/Shanghai'}</div>
                    </div>
                  </div>
                )}

                {Array.isArray(scheduleParseResult.data?.next_runs_display) && scheduleParseResult.data.next_runs_display.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>后续执行时间：</div>
                    <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                      {scheduleParseResult.data.next_runs_display.slice(0, 6).map((t, idx) => (
                        <div key={idx} style={{ marginBottom: 4 }}>
                          {idx + 1}. {t}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(scheduleParseResult.data?.warnings) && scheduleParseResult.data.warnings.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Alert
                      message="警告"
                      description={(
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {scheduleParseResult.data.warnings.map((w, idx) => (
                            <li key={idx}>{w}</li>
                          ))}
                        </ul>
                      )}
                      type="warning"
                      showIcon
                    />
                  </div>
                )}
              </div>
            ) : scheduleParseResult.needsClarification ? (
              <div>
                <Alert
                  message="需要补充信息"
                  description="请根据以下问题补充定时任务信息："
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                {Array.isArray(scheduleParseResult.data?.questions) && scheduleParseResult.data.questions.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>需要回答的问题：</div>
                    <div style={{ background: '#fff7e6', padding: 12, borderRadius: 4, border: '1px solid #ffd591' }}>
                      {scheduleParseResult.data.questions.map((q, idx) => (
                        <div key={idx} style={{ marginBottom: 8, fontWeight: 500 }}>
                          {idx + 1}. {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Alert
                type="error"
                showIcon
                message="解析失败"
                description={formatErrorMessage(scheduleParseResult.error || scheduleParseResult.data?.error_message) || '无法解析定时描述'}
              />
            )}
          </div>
        )}

        <Divider style={{ margin: '8px 0 12px' }} />
        <Text strong>生产参数（与立即生产分开填写）</Text>

        {producerName !== 'xhs_video_importer' && producerName !== 'video_bilingual_convert' && producerName !== 'idea_to_video' && (
          <Form.Item
            name={['params', 'keywords']}
            label="关键词"
            rules={[{ required: ['papers_to_notes', 'papers_to_wechat_article', 'papers_to_video'].includes(producerName), message: '请至少输入 1 个关键词' }]}
          >
            <Select mode="tags" tokenSeparators={KEYWORDS_TOKEN_SEPARATORS} placeholder="输入关键词后回车（支持逗号/空格/换行分隔）" />
          </Form.Item>
        )}

        {producerName === 'xhs_video_importer' && (
          <>
            <Form.Item name={['params', 'extra_params', 'mode']} label="模式">
              <Radio.Group
                options={[
                  { label: '账号模式', value: 'accounts' },
                  { label: '关键词模式', value: 'keywords' },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
            {xhsMode === 'accounts' ? (
              <Form.Item
                name={['params', 'extra_params', 'accountsText']}
                label="账号列表（每行一个账号主页 URL 或 ID）"
                rules={[{ required: true, message: '请输入账号主页 URL 或 ID' }]}
              >
                <Input.TextArea rows={4} placeholder="例如：https://www.xiaohongshu.com/user/profile/xxxx" />
              </Form.Item>
            ) : (
              <Form.Item
                name={['params', 'keywords']}
                label="关键词（搜索用）"
                rules={[{ required: ['papers_to_notes', 'papers_to_wechat_article', 'papers_to_video'].includes(producerName), message: '请至少输入 1 个关键词' }]}
              >
                <Select mode="tags" tokenSeparators={KEYWORDS_TOKEN_SEPARATORS} placeholder="输入关键词后回车（支持逗号/空格/换行分隔）" />
              </Form.Item>
            )}
          </>
        )}

        <Space size="middle" style={{ width: '100%' }}>
          <Form.Item name={['params', 'days']} label="最近 N 天" style={{ flex: 1 }} rules={[{ required: true }]}>
            <InputNumber min={1} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={['params', 'max_count']} label="最多处理条数" style={{ flex: 1 }} rules={[{ required: true }]}>
            <InputNumber min={1} max={200} style={{ width: '100%' }} />
          </Form.Item>
        </Space>

        <Form.Item name={['params', 'creator_user_id']} label="创建者ID">
          <Input placeholder="默认 producer_bot" allowClear />
        </Form.Item>

        <Form.Item name={['params', 'available_platforms']} label="可投递平台（可选）">
          <Select mode="multiple" allowClear placeholder="可留空走后端默认" options={PLATFORM_OPTIONS} />
        </Form.Item>

        {producerName === 'papers_to_notes' && (
          <Space size="middle" style={{ width: '100%' }}>
            <Form.Item name={['params', 'extra_params', 'max_images']} label="最大图片数" style={{ flex: 1 }}>
              <InputNumber min={1} max={50} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name={['params', 'extra_params', 'force']} label="强制执行" style={{ flex: 1 }}>
              <Radio.Group
                options={[
                  { label: '否', value: false },
                  { label: '是', value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </Space>
        )}

        {producerName === 'papers_to_video' && (
          <>
            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'scene_count']} label="场景数量" style={{ flex: 1 }}>
                <InputNumber min={3} max={10} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'duration_sec']} label="视频时长（秒）" style={{ flex: 1 }}>
                <InputNumber min={10} max={120} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'aspect_ratio']} label="视频比例" style={{ flex: 1 }}>
                <Select options={VIDEO_ASPECT_RATIO_OPTIONS} />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'output_language']} label="输出语言" style={{ flex: 1 }}>
                <Radio.Group
                  options={[
                    { label: '中文', value: 'zh' },
                    { label: 'English', value: 'en' },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'image_size']} label="图片尺寸" style={{ flex: 1 }}>
                <Input allowClear placeholder="留空按视频比例自动选择" />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'image_model']} label="图片模型" style={{ flex: 1 }}>
                <Input allowClear />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'image_style']} label="图片风格" style={{ flex: 1 }}>
                <Input allowClear />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'tts_voice']} label="配音音色" style={{ flex: 1 }}>
                <Input allowClear />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'upload_images']} label="上传图片" valuePropName="checked" style={{ flex: 1 }}>
                <Switch />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'force']} label="强制执行" style={{ flex: 1 }}>
                <Radio.Group
                  options={[
                    { label: '否', value: false },
                    { label: '是', value: true },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Space>
          </>
        )}

        {producerName === 'idea_to_video' && (
          <>
            <Form.Item
              name={['params', 'extra_params', 'idea_text']}
              label="创意描述（自然语言）"
              rules={[{ required: true, message: '请输入创意描述' }]}
            >
              <Input.TextArea rows={5} placeholder="例如：做一条关于‘AI 帮科研新人快速读懂论文’的 30 秒视频，开头要抓人，风格偏纪实科技感。" />
            </Form.Item>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'aspect_ratio']} label="画面比例" style={{ flex: 1 }}>
                <Select
                  options={[
                    { label: '9:16（竖屏）', value: '9:16' },
                    { label: '3:4（竖构图）', value: '3:4' },
                    { label: '4:3（横构图）', value: '4:3' },
                    { label: '16:9（横屏）', value: '16:9' },
                    { label: '1:1（方形）', value: '1:1' },
                  ]}
                />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'duration_preset']} label="时长预设" style={{ flex: 1 }}>
                <Select
                  options={[
                    { label: '~15s', value: '~15s' },
                    { label: '~30s', value: '~30s' },
                    { label: '40-60s', value: '40-60s' },
                  ]}
                />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'duration_sec']} label="时长（秒）" style={{ flex: 1 }}>
                <InputNumber min={8} max={180} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'storyboard_count']} label="分镜数量" style={{ flex: 1 }}>
                <InputNumber min={3} max={12} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'video_provider']} label="视频 Provider" style={{ flex: 1 }}>
                <Select options={VIDEO_PROVIDER_OPTIONS} />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'model_name']} label="视频模型（可选）" style={{ flex: 1 }}>
                <Input allowClear placeholder="为空则走后端默认模型" />
              </Form.Item>
            </Space>

            <Form.Item name={['params', 'extra_params', 'style']} label="风格">
              <Input allowClear placeholder="例如：真实电影感 / 二次元 / 极简产品广告" />
            </Form.Item>

            <Form.Item name={['params', 'extra_params', 'referencesText']} label="参考图来源（每行一个 URL 或服务器路径）">
              <Input.TextArea rows={4} placeholder={"https://.../ref1.png\n/data/materials/ref2.jpg"} />
            </Form.Item>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'first_frame_source']} label="首帧（可选）" style={{ flex: 1 }}>
                <Input allowClear placeholder="URL 或服务器路径" />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'last_frame_source']} label="尾帧（可选）" style={{ flex: 1 }}>
                <Input allowClear placeholder="URL 或服务器路径" />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'tts_voice']} label="配音音色（可选）" style={{ flex: 1 }}>
                <Input allowClear placeholder="如 female-tianmei" />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'include_subtitles']} label="叠加字幕" valuePropName="checked" style={{ flex: 1 }}>
                <Switch />
              </Form.Item>
            </Space>

            <Form.Item name={['params', 'extra_params', 'force']} label="强制执行">
              <Radio.Group
                options={[
                  { label: '否', value: false },
                  { label: '是', value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </>
        )}

        {producerName === 'video_bilingual_convert' && (
          <>
            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'target_lang']} label="目标语言" style={{ flex: 1 }}>
                <Select
                  options={[
                    { label: '自动（中文↔英文）', value: 'auto' },
                    { label: '中文 → 英文', value: 'en' },
                    { label: '英文 → 中文', value: 'zh' },
                  ]}
                />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'max_items']} label="最大处理条数" style={{ flex: 1 }}>
                <InputNumber min={1} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Form.Item name={['params', 'extra_params', 'force']} label="强制执行">
              <Radio.Group
                options={[
                  { label: '否', value: false },
                  { label: '是', value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>

            <Divider orientation="left" plain>内容筛选条件</Divider>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <Form.Item label="内容类型" name={['params', 'extra_params', 'filters', 'content_type']}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="请选择内容类型"
                    options={[
                      { label: '视频', value: '视频' },
                      { label: '图文', value: '图文' },
                      { label: '笔记', value: '笔记' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="内容来源" name={['params', 'extra_params', 'filters', 'source']}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="请选择内容来源"
                    options={[
                      { label: '自建', value: '自建' },
                      { label: '抓取', value: '抓取' },
                      { label: 'AI生成', value: 'AI生成' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="内容状态" name={['params', 'extra_params', 'filters', 'status']}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="请选择内容状态"
                    options={[
                      { label: '待发布', value: '待发布' },
                      { label: '发布中', value: '发布中' },
                      { label: '已发布', value: '已发布' },
                      { label: '作废', value: '作废' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="创作者" name={['params', 'extra_params', 'filters', 'creator_user_id']}>
                  <Input allowClear placeholder="请输入创作者ID" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="标题" name={['params', 'extra_params', 'filters', 'title']}>
                  <Input allowClear placeholder="请输入标题" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="内容ID" name={['params', 'extra_params', 'filters', 'content_id']}>
                  <Input allowClear placeholder="请输入内容ID" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="标签" name={['params', 'extra_params', 'filters', 'tags']}>
                  <Input allowClear placeholder="多个标签用逗号分隔" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="投递平台" name={['params', 'extra_params', 'filters', 'available_platforms']}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="请选择投递平台"
                    options={PLATFORM_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="风险状态" name={['params', 'extra_params', 'filters', 'risk_flag']}>
                  <Select
                    allowClear
                    placeholder="请选择风险状态"
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="是否审核" name={['params', 'extra_params', 'filters', 'is_manual_reviewed']}>
                  <Select
                    allowClear
                    placeholder="请选择是否审核"
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="是否需要人工审核" name={['params', 'extra_params', 'filters', 'manual_review_required']}>
                  <Select
                    allowClear
                    placeholder="请选择是否需要人工审核"
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="入库时间" name={['params', 'extra_params', 'filters', 'ingested_at_range']}>
                  <RangePicker style={{ width: '100%' }} showTime={{ format: 'HH:mm:ss' }} format="YYYY-MM-DD HH:mm:ss" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {producerName === 'papers_to_wechat_article' && (
          <>
            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'author']} label="作者" style={{ flex: 1 }}>
                <Input allowClear />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'digest']} label="摘要" style={{ flex: 1 }}>
                <Input allowClear />
              </Form.Item>
            </Space>

            <Form.Item name={['params', 'extra_params', 'tags']} label="标签（可选）">
              <Select mode="tags" placeholder="输入 tag 后回车" />
            </Form.Item>

            <Form.Item name={['params', 'extra_params', 'force']} label="强制执行">
              <Radio.Group
                options={[
                  { label: '否', value: false },
                  { label: '是', value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </>
        )}

        {producerName === 'xhs_video_importer' && (
          <>
            <Form.Item name={['params', 'extra_params', 'headless']} label="无头模式" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'login_wait']} label="登录等待（秒）" style={{ flex: 1 }}>
                <InputNumber min={0} max={600} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'max_scroll']} label="最大滚动次数" style={{ flex: 1 }}>
                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'scroll_pause']} label="滚动间隔（秒）" style={{ flex: 1 }}>
                <InputNumber min={0} max={60} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'note_list_type']} label="笔记列表类型" style={{ flex: 1 }}>
                <Select
                  allowClear
                  options={[
                    { label: '已发布', value: 'published' },
                    { label: '点赞', value: 'liked' },
                    { label: '收藏', value: 'collected' },
                  ]}
                />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'video_only']} label="仅视频" style={{ flex: 1 }}>
                <Radio.Group
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'deduplicate_across_keywords']} label="跨关键词去重" style={{ flex: 1 }}>
                <Radio.Group
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Space>

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item name={['params', 'extra_params', 'deduplicate_across_accounts']} label="跨账号去重" style={{ flex: 1 }}>
                <Radio.Group
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name={['params', 'extra_params', 'force']} label="强制执行" style={{ flex: 1 }}>
                <Radio.Group
                  options={[
                    { label: '否', value: false },
                    { label: '是', value: true },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Space>
          </>
        )}
      </Form>

      <ProduceRunHistoryDrawer
        open={historyOpen}
        schedule={infoSchedule}
        onClose={() => setHistoryOpen(false)}
      />

      <Drawer
        title="运行详情"
        open={runViewerOpen}
        onClose={() => setRunViewerOpen(false)}
        width={720}
        destroyOnClose
      >
        <ProducerRunViewer
          runId={activeRunId}
          producerName={infoSchedule?.producer_name || producerName}
          title="运行进度"
        />
      </Drawer>
    </Drawer>
  );
};

export default ProducerScheduleEditorModal;
